import { useNavigate } from '@tanstack/react-router'
import { Tent } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useLibrary } from '../hooks/useLibrary'
import { usePageTitle } from '../hooks/usePageTitle'
import { LEGACY_PRODUCT_NAME } from '../lib/product'
import { Button, EmptyState, GameTile, Skeleton, SkeletonGroup } from '../components/ui'
import { formatPlaytime, librarySyncState, steamStoreUrl } from '../lib/format'

/**
 * The logged-in user's synced Steam library: a cover-art grid of owned games
 * with title + playtime. Read-only. Sorted by title server-side; playtime is a
 * neutral stat, NEVER a ranking (playtime is confidence, not preference — the
 * fireside research guardrail). Art is grounded from the real appid.
 */
export function LibraryScreen() {
  usePageTitle('Your library', LEGACY_PRODUCT_NAME)
  const { user } = useAuth()
  const { games, isLoading, error } = useLibrary()
  const navigate = useNavigate()

  // Empty-library copy must stay honest across the sync lifecycle (§5.2): a
  // completed-but-empty sync is NOT "not synced yet". Mirrors the profile
  // card's language for the same states.
  const syncState = librarySyncState(user)
  const emptyCopy: { title: string; description: string; action?: React.ReactNode } =
    syncState === 'never_synced'
      ? {
          title: 'Your library isn’t synced yet',
          description:
            'Turn on Personalized recommendations on your profile to sync your Steam library.',
          action: <Button onClick={() => navigate({ to: '/steam/profile' })}>Go to your profile</Button>,
        }
      : syncState === 'syncing'
        ? {
            title: 'Syncing your Steam library',
            description:
              'We’re waiting on your first sync — it usually lands in under a minute. If your Steam game details are set to private, we can’t read your library.',
          }
        : {
            title: 'No games to show',
            description: 'Your library synced, but this Steam account doesn’t own any games yet.',
          }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-3xl leading-tight text-text-strong">Your library</h1>
        <p className="mt-1 text-sm text-text-muted">
          {games.length > 0
            ? `${games.length} ${games.length === 1 ? 'game' : 'games'} synced from your Steam account`
            : 'Games synced from your Steam account'}
        </p>
      </header>

      {error && <p className="text-sm text-danger-fg">{error}</p>}

      {isLoading ? (
        <SkeletonGroup
          label="Loading your library"
          className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4"
        >
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i}>
              <Skeleton className="aspect-[460/215] w-full rounded-card" />
              <Skeleton className="mt-2 h-4 w-3/4" />
              <Skeleton className="mt-1 h-3 w-1/3" />
            </div>
          ))}
        </SkeletonGroup>
      ) : games.length === 0 ? (
        // A failed fetch proves nothing about ownership — never assert "this
        // account owns no games" over an error (§5.2 honesty).
        !error && (
          <EmptyState
            icon={<Tent className="h-10 w-10" strokeWidth={1.5} />}
            title={emptyCopy.title}
            description={emptyCopy.description}
            action={emptyCopy.action}
          />
        )
      ) : (
        <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {games.map((game) => (
            <li key={game.appid}>
              <GameTile
                appid={game.appid}
                name={game.name}
                artSrc={game.header_image}
                caption={formatPlaytime(game.playtime_forever)}
                href={steamStoreUrl(game.appid)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
